<script setup lang="ts">
import * as ol from 'ol';
import type { Coordinate } from 'ol/coordinate';
import * as extent from 'ol/extent';
import GeoJSON from 'ol/format/GeoJSON';
import Point from 'ol/geom/Point';
import VectorLayer from 'ol/layer/Vector';
import * as proj from 'ol/proj';
import VectorSource from 'ol/source/Vector';
import Circle from 'ol/style/Circle';
import Fill from 'ol/style/Fill';
import Style from 'ol/style/Style';
import {
  computed,
  onMounted,
  ref,
  watch,
} from 'vue';

import {
  NoraSupportedCoordinateFormats,
} from '@nora-monorepo/shared';

import { createDefaultBaseMapLayer } from '#nora-ui/map/map/base-map-layer-factory';
import { getDefaultMapCenter } from '#nora-ui/map/map/MapSetup';

interface MiniMapProps {
  width: number;
  height: number;
  center?: {
    latitude: number;
    longitude: number;
  };
  marker?: {
    latitude: number;
    longitude: number;
  };
  geojsonFeature?: GeoJSON.Feature;
}

const props = defineProps<MiniMapProps>();
const mapDomElement = ref<HTMLElement | undefined>(undefined);

const style = computed(() => ({
  width: `${props.width}px`,
  height: `${props.height}px`,
}));

onMounted(() => {
  renderMiniMap();
});

watch(
  () => props.marker,
  () => {
    renderMiniMap();
  },
);

watch(
  () => props.geojsonFeature,
  () => {
    renderMiniMap();
  },
);

function renderMiniMap() {
  if (mapDomElement.value) {
    mapDomElement.value.innerHTML = '';
  }

  const center = getCenter();

  const map = new ol.Map({
    layers: [createDefaultBaseMapLayer()],
    target: mapDomElement.value,
    view: new ol.View({
      center: proj.fromLonLat(center),
      zoom: 10,
    }),
    controls: [],
  });

  if (props.marker) {
    const coordinates = [props.marker.longitude, props.marker.latitude];

    addMarkerToMap(map, coordinates);
  }

  if (props.geojsonFeature) {
    addGeoJSONFeatureToMap(map, props.geojsonFeature);
  }
}

function addMarkerToMap(map: ol.Map, coordinates: Coordinate) {
  const marker = new ol.Feature({
    geometry: new Point(proj.fromLonLat(coordinates)),
  });

  marker.setStyle(
    new Style({
      image: new Circle({
        radius: 5,
        fill: new Fill({
          color: 'blue',
        }),
      }),
    }),
  );

  const vectorSource = new VectorSource({
    features: [marker],
  });

  const vectorLayer = new VectorLayer({
    source: vectorSource,
  });

  map.addLayer(vectorLayer);
}

function addGeoJSONFeatureToMap(map: ol.Map, geojsonFeature: GeoJSON.Feature) {
  const format = new GeoJSON();
  const feature = format.readFeature(geojsonFeature, {
    featureProjection: NoraSupportedCoordinateFormats.WEB_MERCATOR, // Project GeoJSON to the map's coordinate system
  });

  const vectorSource = new VectorSource({
    features: Array.isArray(feature) ? feature : [feature],
  });

  const vectorLayer = new VectorLayer({
    source: vectorSource,
  });

  map.addLayer(vectorLayer);

  const featureExtent = getExtent(feature);

  if (featureExtent) {
    map.getView().fit(featureExtent, {
      size: map.getSize(),
      maxZoom: 18,
      padding: [50, 50, 50, 50],
    });
  }
}

function getExtent(features: ol.Feature | ol.Feature[]) {
  if (!Array.isArray(features)) {
    return features.getGeometry()?.getExtent();
  }

  const fullExtent = extent.createEmpty();

  features.forEach((feature) => {
    const geometryExtent = feature.getGeometry()?.getExtent();

    if (geometryExtent) {
      extent.extend(fullExtent, geometryExtent);
    }
  });

  return fullExtent;
}

function getCenter() {
  if (props.center) {
    return [props.center.longitude, props.center.latitude];
  }

  if (props.marker) {
    return [props.marker.longitude, props.marker.latitude];
  }

  if (props.geojsonFeature) {
    const format = new GeoJSON();
    const feature = format.readFeature(props.geojsonFeature, {
      featureProjection: 'EPSG:3857',
    });

    const featureExtent = getExtent(feature);

    if (featureExtent) {
      const center = extent.getCenter(featureExtent);
      const centerInLatLong = proj.transform(center, NoraSupportedCoordinateFormats.WEB_MERCATOR, NoraSupportedCoordinateFormats.WGS84);

      return centerInLatLong;
    }
  }

  const defaultCenter = getDefaultMapCenter();

  return [defaultCenter.longitude, defaultCenter.latitude];
}
</script>

<template>
  <div
    class="w-full h-full"
    :style="style"
  >
    <div
      ref="mapDomElement"
      class="w-full h-full"
    />
  </div>
</template>
